import { toast } from "react-toastify";
import { menuItems, IMenuItem } from "./../global/Sidebar/MenuItems";
import moment from "moment";
import { ChangeEvent } from "react";
export const dateFormatter = (value: string) => {
  const timestamp = Date.parse(value);
  if (value && !isNaN(timestamp)) {
    const date = new Date(value);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0"); // Month starts from 0, so add 1
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  } else {
    return null;
  }
};
export function formatDateForAthena(value: string) {
  const timestamp = Date.parse(value);
  if (value && !isNaN(timestamp)) {
    const date = new Date(value);
    const month = String(date.getMonth() + 1).padStart(2, "0"); // Months are zero-based
    const day = String(date.getDate()).padStart(2, "0");
    const year = date.getFullYear();

    const hours = String(date.getHours()).padStart(2, "0");
    const minutes = String(date.getMinutes()).padStart(2, "0");
    return `${month}/${day}/${year} ${hours}:${minutes}`;
  } else {
    return null;
  }
}
const compareRoutePaths = (path2: string, path1: string) => {
  // Split the paths into segments
  const segments1 = path1.split("/");
  const segments2 = path2.split("/");
  let pathCount = 0;
  // Check if both paths have the same number of segments
  if (segments1.length !== segments2.length) {
    return {};
  }

  // Compare each segment
  for (let i = 0; i < segments1.length; i++) {
    if (segments2[i].startsWith(":")) {
      // Skip segments that are route parameters (e.g., :id)
      pathCount = i;
      continue;
    } else if (segments1[i] !== segments2[i]) {
      return {};
    }
  }

  // All non-parameter segments matched
  return { isValid: true, id: segments1[pathCount] };
};

export const getSideBarActiveDetails = (pathname: string) => {
  let menuItemDetails;
  let subMenuDetails;
  let pageDetails = null;
  menuItems.forEach((menuItem: IMenuItem) => {
    menuItem.subMenus?.find((item) => {
      if (item.path === pathname) {
        menuItemDetails = menuItem;
        subMenuDetails = item;
        return item;
      } else {
        item.pages?.find((page) => {
          const { isValid, id } = compareRoutePaths(page.path, pathname);

          if (isValid) {
            const pageWithId = {
              name: page.name,
              path: page.path.replace(":id", id),
            };
            menuItemDetails = menuItem;
            subMenuDetails = item;
            pageDetails = pageWithId;
            return item;
          }
          return null;
        });
      }
      return null;
    });
  });
  return { menuItemDetails, subMenuDetails, pageDetails };
};
export const formatDate = (date: string) => {
  try {
    const newDate = new Date(date);
    return newDate.toLocaleDateString("en-GB");
  } catch {
    return date;
  }
};

export const getErrorMessage = (error: any) => {
  // Handle 500 Internal Server Error
  if (error.status === 500) {
    toast.error("Internal Server Error. Please try again later.");
    return "Internal Server Error. Please try again later.";
  }

  let data = error?.data;

  // Parse error.data if it's a string
  if (typeof data === "string") {
    try {
      data = JSON.parse(data);
    } catch (e) {
      toast.error("Unexpected error format.");
      return "Unexpected error format.";
    }
  }

  // Handle result message with failure status
  if (data?.result?.msg && data?.result?.status === "F") {
    showToastIfAllowed(data.result.msg);
    return data.result.msg;
  }

  // Handle validation errors (field-level)
  if (data?.errors && typeof data.errors === "object") {
    Object.entries(data.errors).forEach(([field, messages]) => {
      (messages as string[]).forEach((msg) => {
        showToastIfAllowed(`${field}: ${msg}`);
      });
    });
    return data.errors;
  }

  // Handle 502, 503, 504 - typical ECS/service unavailability errors
  if (error?.data?.status === 502 || error?.data?.status === 503 || error?.data?.status === 504) {
    const ecsMessage = "Service is currently unavailable. The ECS service may be stopped or experiencing technical issues. Please contact support@trinetmedical.com";
    toast.error(ecsMessage, { autoClose: 5000 });
    return ecsMessage;
  }

  // Handle network errors or CORS issues
  if (!error?.data?.status || error?.data?.status === 0 || error?.data?.name === 'NetworkError') {
    const corsMessage = "Network error occurred. This might be due to CORS policy or connectivity issues. Please check your connection or contact support.";
    toast.error(corsMessage);
    return corsMessage;
  }

  // Handle generic fallback messages
  const fallbackMsg = data?.message || data?.title || data?.result;
  showToastIfAllowed(fallbackMsg, { autoClose: 3000 });
  return fallbackMsg;
};

export const showToastIfAllowed = (msg?: string, options?: any) => {
  if (!msg) return;

  const avoidableMsgs = ["tenantinfo is not valid"].map(m => m.toLowerCase());
  const normalizedMsg = msg.toLowerCase().trim();

  if (avoidableMsgs.includes(normalizedMsg)) {
    return; // Do not show toast
  }

  toast.error(msg, options);
};

export const getFilters = (params: any) => {
  return Object.keys(params)
    .filter(
      (key) =>
        params[key] !== "" && params[key] !== null && params[key] !== undefined
    )
    .map(
      (key) => encodeURIComponent(key) + "=" + encodeURIComponent(params[key])
    )
    .join("&");
};

export const toggleClass = () => {
  document.body.classList.toggle("customeModalOpen");
};

export const trimObjectValues = (obj: any) => {
  return Object.keys(obj).reduce((acc: any, key) => {
    const value = obj[key];
    acc[key] = typeof value === "string" ? value.replace(/^\s+/g, "") : value;
    return acc;
  }, {});
};

export const checkTokenExpiration = (exp: number | null, time = 0) => {
  const currentTime = Date.now() / 1000 - time;
  return exp && exp < currentTime;
};

export const currencyFormatter = (amount: number) => {
  // Format without rounding off to 2 decimals
  const formattedAmount = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0, // show decimals only if needed
    maximumFractionDigits: 4, // allow up to 4 decimal places
    useGrouping: true, // optional: adds commas
  }).format(amount);

  return formattedAmount;
};

export const MAX_IMAGE_FILE_SIZE = 500 * 1024; // 5KB

export const ALLOWED_IMAGE_FILE_TYPES = [
  "image/jpeg",
  "image/png",
  "image/jpg",
];

export const getBarcodeVales = (selectedBarcodeItem: any) => {
  const formattedDate = moment(selectedBarcodeItem?.expiryDate).format(
    "MM/DD/YYYY"
  );
  const clientID = localStorage.getItem("profileClientId");
  return {
    Barcodes: [
      {
        Key: "imageid1",
        Value: `${selectedBarcodeItem?.rxSRXId}`, //"0100300064827019173012251020240828-1DC5BD371"
      },
      {
        Key: "imageid2",
        Value: `${selectedBarcodeItem?.rxSRXId}`, // "0100300064827019173012251020240828-1DC5BD371"
      },
      {
        Key: "imageNdc",
        Value: selectedBarcodeItem?.ndcCode, //"0006-4827-01"
      },
      {
        Key: "imageLot",
        Value: selectedBarcodeItem?.lotNo, //"20240828-1DC5BD371"
      },
      {
        Key: "imageExpDate",
        Value: formattedDate, //"12/30/2025"
      },
      {
        Key: "imageLotExpDate",
        Value: `${selectedBarcodeItem?.lotNo}\t\t${formattedDate}`, //"20240828-1DC5BD371\t\t12/30/2025"
      },
    ],
    Data: [
      {
        Key: "ClientId",
        Value: clientID, //"STAGE"
      },
      {
        Key: "Source",
        Value: selectedBarcodeItem?.stock?.stockCode, //"340B"
      },
      {
        Key: "LotNo",
        Value: selectedBarcodeItem?.lotNo, //"20240828-1DC5BD371"
      },
      {
        Key: "RxName",
        Value: selectedBarcodeItem?.rxName, //"01A VARIVAX"
      },
      {
        Key: "Manufacturer",
        Value: selectedBarcodeItem?.manufacturer, //"Merck Sharp & Dohme Corp."
      },
      {
        Key: "UserDef1",
        Value: "-1",
      },
      {
        Key: "ExpDate",
        Value: formattedDate, //"12/30/2025"
      },
      {
        Key: "NDC10",
        Value: selectedBarcodeItem?.ndcCode, //"0006-4827-01"
      },
      {
        Key: "Barcode",
        Value: selectedBarcodeItem?.rxSRXId, //"0100300064827019173012251020240828-1DC5BD371"
      },
      {
        Key: "Row1Text",
        Value: `${selectedBarcodeItem?.rxName}-${selectedBarcodeItem?.manufacturer}-${selectedBarcodeItem?.stock?.stockCode}`, //"01A VARIVAX-Merck Sharp & Dohme -340B"
      },
      {
        Key: "Row2Text",
        Value: `${selectedBarcodeItem?.ndcCode}-${selectedBarcodeItem?.lotNo}-${formattedDate}`, //"0006-4827-01-20240828-1DC5BD371-12/30/2025"
      },
      {
        Key: "Row3Text",
        Value: `${selectedBarcodeItem?.rxSRXId}-${clientID}`, //"0100300064827019173012251020240828-1DC5BD371-STAGE"
      },
      {
        Key: "LotExpDate",
        Value: `${selectedBarcodeItem?.lotNo}\t\t${formattedDate}`, //"20240828-1DC5BD371\t\t12/30/2025"
      },
      {
        Key: "PatientName",
        Value: "Test Trinet",
      },
      {
        Key: "ProviderName",
        Value: "Chris Buckley",
      },
      {
        Key: "DepartmentName",
        Value: "7 Hills Department (150)",
      },
      {
        Key: "PrintDate",
        Value: moment(new Date()).format("DD/MM/YYYY"), //"11/22/2024"
      },
    ],
  };
};

export const manageLineBreakFromString = (message: string) => {
  if (!message) return "";
  return message.replace(/\\n|\n/g, "<br/>");
};

export const splitPasswordPolicyError = (text?: string): string[] => {
  if (!text || typeof text !== "string") return [];
  const results: string[] = [];
  const sentences = text.split(".");
  sentences.forEach((sentence) => {
    sentence.split(",").forEach((part) => {
      const cleaned = part.replace(/\s+/g, " ").trim();
      if (cleaned) results.push(cleaned);
    });
  });
  return results;
};

export const handleNumberInput = (e: any) => {
  let value = e.target.value;

  // Remove all characters except digits and dots
  value = value.replace(/[^0-9.]/g, "");

  // Allow only one dot
  const parts = value.split(".");
  if (parts.length > 2) {
    value = parts[0] + "." + parts.slice(1).join("");
  }

  e.target.value = value;
};

export const itemPagesettingName = "ItemPageSettings";

export const dashboardColors = [
  "#5574a3",
  "#7aa0d1",
  "#4d9b9d",
  "#7cc6b5",
  "#f3ab61",
  "#da87a5",
];

export const ndcRegex = /^(?:\d{4}-\d{4}-\d{2}|\d{5}-(?:\d{3}-\d{2}|\d{4}-\d{1,2}))$/;

export const ensurePeriod = (text: string) => {
  return text.endsWith(".") ? text : `${text}.`;
};

export const beautifyJSON = (data: any, indent: number = 4): string => {
  if (data === null || data === undefined) {
    return "";
  }

  if (typeof data === "object") {
    try {
      return JSON.stringify(data, null, indent);
    } catch (error) {
      console.error("Error stringifying object:", error);
      return String(data);
    }
  }

  if (typeof data === "string") {
    if (data.trim() === "") {
      return "";
    }

    try {
      const parsed = JSON.parse(data);
      return JSON.stringify(parsed, null, indent);
    } catch (error) {
      return data;
    }
  }

  return String(data);
};

export const parseBoolean = (value: any): boolean | null => {
  if (typeof value === 'boolean') return value;

  if (typeof value === 'string') {
    const normalized = value.toLowerCase();
    if (normalized === 'true') return true;
    if (normalized === 'false') return false;
  }

  return null;
};

export const formatDateForReports = (date: Date) => {
  if (!date) return null;
  return moment(date).format("YYYY-MM-DDTHH:mm:ss.SSS[Z]");
}

export const truncateText = (value: string, limit: number): string => {
  if (!value) return "";

  if (value.length <= limit) return value;

  return value.slice(0, limit).trimEnd() + "...";
};
